import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import matchService, { MatchGame, UpdateMatchData, MatchPair } from '../services/matchService';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { stripHtmlTags } from '../utils/htmlUtils';
import { 
  ArrowLeftIcon, 
  PlayIcon, 
  PencilIcon, 
  TrashIcon,
  XMarkIcon,
  PlusIcon,
  GlobeAltIcon,
  LockClosedIcon,
  PuzzlePieceIcon
} from '@heroicons/react/24/outline';

const MatchDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [match, setMatch] = useState<MatchGame | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [togglingPublic, setTogglingPublic] = useState(false);
  const [showMatchOptions, setShowMatchOptions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  
  // Enhanced filter states
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'order' | 'front' | 'back'>('order');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showCreatePairModal, setShowCreatePairModal] = useState(false);
  const [editingPairIndex, setEditingPairIndex] = useState<number | null>(null);
  
  // Create/Edit pair form state
  const [newFront, setNewFront] = useState('');
  const [newBack, setNewBack] = useState('');
  const [savingPair, setSavingPair] = useState(false);
  
  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (id) {
      loadMatch();
    }
  }, [id]);

  const loadMatch = async () => {
    try {
      setLoading(true);
      // Extract the ID from the slug if it contains one (format: title-slug-id)
      const matchId = id!.includes('-') ? id!.split('-').pop() : id;
      const matchData = await matchService.getMatchGame(matchId!);
      setMatch(matchData);
      
      // Initialize edit form with current data
      setEditTitle(matchData.title);
      setEditDescription(matchData.description || '');
      setEditTags(matchData.tags || []);
    } catch (error) {
      console.error('Error loading match:', error);
      toast.error('Erro ao carregar match');
      navigate('/matches');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) {
      toast.error('O título não pode estar vazio');
      return;
    }

    try {
      const matchId = id!.includes('-') ? id!.split('-').pop() : id;
      const updateData: UpdateMatchData = {
        title: editTitle,
        description: editDescription,
        tags: editTags,
      };

      await matchService.updateMatch(matchId!, updateData);
      toast.success('Match atualizado com sucesso!');
      setIsEditing(false);
      loadMatch();
    } catch (error) {
      console.error('Error updating match:', error);
      toast.error('Erro ao atualizar match');
    }
  };

  const handleDeleteMatch = async () => {
    try {
      const matchId = id!.includes('-') ? id!.split('-').pop() : id;
      await matchService.deleteMatch(matchId!);
      toast.success('Match deletado com sucesso!');
      navigate('/matches');
    } catch (error) {
      console.error('Error deleting match:', error);
      toast.error('Erro ao deletar match');
    }
  };

  const handleTogglePublic = async () => {
    if (!id || !match) return;

    try {
      setTogglingPublic(true);
      const matchId = id!.includes('-') ? id!.split('-').pop() : id;
      const newPublicStatus = !match.isPublic;
      await matchService.updateMatch(matchId!, {
        isPublic: newPublicStatus,
      });
      setMatch({ ...match, isPublic: newPublicStatus });
      toast.success(newPublicStatus ? 'Match agora é público!' : 'Match agora é privado!');
    } catch (error) {
      console.error('Failed to toggle public status:', error);
      toast.error('Erro ao alterar visibilidade do match');
    } finally {
      setTogglingPublic(false);
    }
  };

  const handlePlayMatch = () => {
    const matchId = id!.includes('-') ? id!.split('-').pop() : id;
    navigate(`/matches/${matchId}/play`);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !editTags.includes(newTag.trim())) {
      setEditTags([...editTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setEditTags(editTags.filter(tag => tag !== tagToRemove));
  };

  const handleOpenCreatePairModal = () => {
    setEditingPairIndex(null);
    setNewFront('');
    setNewBack('');
    setShowCreatePairModal(true);
  };

  const handleOpenEditPairModal = (index: number) => {
    const pair = match!.pairs[index];
    setEditingPairIndex(index);
    setNewFront(pair.front);
    setNewBack(pair.back);
    setShowCreatePairModal(true);
  };

  const handleClosePairModal = () => {
    setShowCreatePairModal(false);
    setEditingPairIndex(null);
    setNewFront('');
    setNewBack('');
  };

  const handleSavePair = async () => {
    if (!newFront.trim()) {
      toast.error('O termo não pode estar vazio');
      return;
    }

    if (!newBack.trim()) {
      toast.error('A definição não pode estar vazia');
      return;
    }

    try {
      setSavingPair(true);
      const matchId = id!.includes('-') ? id!.split('-').pop() : id;
      
      const updatedPairs = [...match!.pairs];
      const pairData = {
        front: newFront,
        back: newBack,
      };

      if (editingPairIndex !== null) {
        // Edit existing pair
        updatedPairs[editingPairIndex] = {
          ...updatedPairs[editingPairIndex],
          ...pairData,
        };
      } else {
        // Add new pair
        updatedPairs.push({
          id: Date.now().toString(),
          ...pairData,
        });
      }

      // Remove 'id' and 'cardId' fields from pairs before sending to backend
      const pairsForBackend = updatedPairs.map(({ id, cardId, ...rest }) => rest);

      await matchService.updateMatch(matchId!, {
        pairs: pairsForBackend,
      });

      toast.success(editingPairIndex !== null ? 'Par atualizado com sucesso!' : 'Par criado com sucesso!');
      handleClosePairModal();
      loadMatch();
    } catch (error) {
      console.error('Error saving pair:', error);
      toast.error('Erro ao salvar par');
    } finally {
      setSavingPair(false);
    }
  };

  const handleDeletePair = async (index: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este par?')) {
      return;
    }

    try {
      const matchId = id!.includes('-') ? id!.split('-').pop() : id;
      const updatedPairs = match!.pairs.filter((_, i) => i !== index);
      
      // Remove 'id' and 'cardId' fields from pairs before sending to backend
      const pairsForBackend = updatedPairs.map(({ id, cardId, ...rest }) => rest);
      
      await matchService.updateMatch(matchId!, {
        pairs: pairsForBackend,
      });
      toast.success('Par deletado com sucesso!');
      loadMatch();
    } catch (error) {
      console.error('Error deleting pair:', error);
      toast.error('Erro ao deletar par');
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Apply client-side filtering and sorting for match pairs
  const getFilteredAndSortedPairs = () => {
    if (!match || !match.pairs) return [];
    
    let filtered = [...match.pairs];

    // Apply search filter (strip HTML so bold/italic formatting doesn't break matching)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(pair =>
        stripHtmlTags(pair.front).toLowerCase().includes(searchLower) ||
        stripHtmlTags(pair.back).toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let compareValue = 0;
      
      switch (sortBy) {
        case 'order':
          // Keep original order using index
          const indexA = match.pairs.findIndex(p => p.id === a.id);
          const indexB = match.pairs.findIndex(p => p.id === b.id);
          compareValue = indexA - indexB;
          break;
        case 'front':
          compareValue = a.front.localeCompare(b.front);
          break;
        case 'back':
          compareValue = a.back.localeCompare(b.back);
          break;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  };

  const displayPairs = match ? getFilteredAndSortedPairs() : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600 dark:text-gray-400">Match não encontrado</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-500 px-px md:p-6">
      {/* Header */}
      <div className="mb-6">
        {/* Mobile-style back button and title */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/matches')}
                className="inline-flex items-center p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 mr-3"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-tight">
                {match.title}
              </h1>
            </div>
            
            {/* Pair count and action buttons - shown on desktop */}
            <div className="hidden sm:flex items-center space-x-2">
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <PuzzlePieceIcon className="w-4 h-4 mr-1" />
                <span>{match.pairs.length} {match.pairs.length === 1 ? 'par' : 'pares'}</span>
              </div>
              
              {/* Edit and Delete buttons */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleTogglePublic}
                  disabled={togglingPublic}
                  className={`p-2 rounded-lg transition-all ${togglingPublic ? 'opacity-50 cursor-not-allowed' : ''} ${
                    match.isPublic 
                      ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                  }`}
                  title={match.isPublic ? 'Match Público - Clique para tornar privado' : 'Match Privado - Clique para tornar público'}
                >
                  {match.isPublic ? (
                    <GlobeAltIcon className="h-5 w-5" />
                  ) : (
                    <LockClosedIcon className="h-5 w-5" />
                  )}
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-pink-600 dark:hover:text-pink-400 hover:bg-pink-50 dark:hover:bg-pink-900/30 rounded-lg transition-all"
                  title="Editar Match"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                  title="Deletar Match"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Pair count and action buttons - shown on mobile */}
          <div className="sm:hidden ml-14 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <PuzzlePieceIcon className="w-4 h-4 mr-1" />
                <span>{match.pairs.length} {match.pairs.length === 1 ? 'par' : 'pares'}</span>
              </div>
              
              {/* Edit and Delete buttons - mobile */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleTogglePublic}
                  disabled={togglingPublic}
                  className={`p-2 rounded-lg transition-all ${togglingPublic ? 'opacity-50 cursor-not-allowed' : ''} ${
                    match.isPublic 
                      ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                  }`}
                  title={match.isPublic ? 'Match Público - Clique para tornar privado' : 'Match Privado - Clique para tornar público'}
                >
                  {match.isPublic ? (
                    <GlobeAltIcon className="h-5 w-5" />
                  ) : (
                    <LockClosedIcon className="h-5 w-5" />
                  )}
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-pink-600 dark:hover:text-pink-400 hover:bg-pink-50 dark:hover:bg-pink-900/30 rounded-lg transition-all"
                  title="Editar Match"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                  title="Deletar Match"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Description */}
          {match.description && (
            <p className="text-gray-700 dark:text-gray-300 mt-4 text-sm sm:text-base bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-xl p-4 shadow-lg">
              {match.description}
            </p>
          )}

          {/* Action Buttons Row */}
          <div className="mt-4 flex flex-wrap gap-3">
            {/* Add Pair Button */}
            {match.type === 'custom' && (
              <button
                onClick={handleOpenCreatePairModal}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-transparent text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg touch-target hover:scale-105 text-white bg-gradient-to-r from-pink-500 via-purple-500 to-pink-600 hover:from-pink-600 hover:via-purple-600 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 dark:focus:ring-offset-gray-900 shadow-pink-500/25 hover:shadow-pink-600/30 backdrop-blur-sm"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Adicionar Par
              </button>
            )}
            
            {/* Play Match Button */}
            {match.pairs.length > 0 && (
              <button
                onClick={handlePlayMatch}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-transparent text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg touch-target text-white bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-600 hover:from-emerald-600 hover:via-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 dark:focus:ring-offset-gray-900 hover:scale-105 shadow-emerald-500/25 hover:shadow-emerald-600/30 backdrop-blur-sm"
              >
                <PlayIcon className="w-4 h-4 mr-2" />
                Jogar Match
              </button>
            )}
            
            {/* Options Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowMatchOptions(!showMatchOptions)}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-gray-200/50 dark:border-gray-600/50 text-sm font-semibold rounded-xl text-gray-700 dark:text-gray-200 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-all duration-200 shadow-lg hover:shadow-xl touch-target backdrop-blur-sm hover:scale-105"
                title="Mostrar opções do match"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
                Opções
              </button>
              
              {/* Dropdown menu */}
              {showMatchOptions && (
                <>
                  {/* Backdrop to close dropdown when clicking outside */}
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowMatchOptions(false)}
                  ></div>
                  
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 z-20 overflow-hidden">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowStatisticsModal(true);
                          setShowMatchOptions(false);
                        }}
                        className="w-full flex items-center px-4 py-3 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Ver Estatísticas
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Mode Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-6 border border-gray-200 dark:border-gray-700 max-w-2xl shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-xl bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Editar Match
              </h3>
              <button
                onClick={() => setIsEditing(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Título
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400 focus:border-transparent transition-all"
                  placeholder="Título do match"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400 focus:border-transparent transition-all resize-none"
                  placeholder="Descrição do match (opcional)"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {editTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-pink-600 dark:text-pink-400 hover:text-pink-800 dark:hover:text-pink-200"
                      >
                      <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400 focus:border-transparent transition-all"
                    placeholder="Adicionar tag"
                  />
                  <button
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Adicionar
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setIsEditing(false)}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all"
              >
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pairs List (for all match types) */}
      {!isEditing && (
        <>
          {/* Search Bar with Filter Button */}
          <div className="mb-4 space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Buscar pares..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 sm:py-2 border border-gray-200/50 dark:border-gray-600/50 rounded-2xl leading-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 dark:focus:ring-pink-400/50 dark:focus:border-pink-400/50 transition-all duration-300 shadow-lg dark:shadow-xl hover:shadow-xl dark:hover:shadow-2xl text-base sm:text-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center touch-target"
                  >
                    <svg className="h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Filter Toggle Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-3 sm:py-2 rounded-2xl transition-all shadow-lg hover:shadow-xl flex items-center gap-2 ${
                  showFilters
                    ? 'bg-pink-600 text-white'
                    : 'bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-600/50'
                }`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="hidden sm:inline text-sm font-medium">Filtros</span>
              </button>
            </div>

            {/* Expandable Filters Panel */}
            {showFilters && (
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-600/50 shadow-xl p-4 space-y-4">
                {/* Sort Options */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ordenar por
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as 'order' | 'front' | 'back')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    >
                      <option value="order">Ordem Original</option>
                      <option value="front">Frente (A-Z)</option>
                      <option value="back">Verso (A-Z)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ordem
                    </label>
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    >
                      <option value="asc">Crescente</option>
                      <option value="desc">Decrescente</option>
                    </select>
                  </div>
                </div>

                {/* Clear Sorting */}
                {sortBy !== 'order' && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSortBy('order');
                      setSortOrder('asc');
                    }}
                    className="w-full px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    Resetar Ordenação
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50">
            <div className="px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-gray-50/50 via-white/30 to-gray-50/50 dark:from-gray-700/30 dark:via-gray-800/30 dark:to-gray-700/30 rounded-t-2xl backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                  Pares ({displayPairs.length > 0 ? `${displayPairs.length} exibidos` : '0'})
                </h3>
                {searchTerm && displayPairs.length !== match.pairs.length && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    de {match.pairs.length} total
                  </span>
                )}
              </div>
            </div>

            {displayPairs.length === 0 && match.pairs.length > 0 ? (
              <div className="text-center py-12 px-4">
                <div className="text-gray-500 dark:text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Nenhum par encontrado</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Nenhum par corresponde à sua busca por "{searchTerm}". Tente ajustar seus termos de busca.
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="inline-flex items-center px-4 py-2 border border-gray-200/50 dark:border-gray-600/50 text-sm font-semibold rounded-xl text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 shadow-lg hover:shadow-xl backdrop-blur-sm hover:scale-105 transition-all duration-200"
                >
                  Limpar Busca
                </button>
              </div>
            ) : match.pairs.length === 0 ? (
              <div className="text-center py-12 px-4">
                <div className="text-gray-500 dark:text-gray-400 mb-4">
                  <PuzzlePieceIcon className="mx-auto h-12 w-12" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Ainda não há pares</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Comece adicionando seu primeiro par a este match.</p>
                <button
                  onClick={handleOpenCreatePairModal}
                  className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-pink-500 via-purple-500 to-pink-600 hover:from-pink-600 hover:via-purple-600 hover:to-pink-700 shadow-lg shadow-pink-500/25 transition-all duration-200 hover:scale-105"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Adicionar Primeiro Par
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {displayPairs.map((pair, index) => {
                    const originalIndex = match.pairs.findIndex(p => p.id === pair.id);
                    return (
                      <div
                        key={pair.id}
                        className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-4">
                            <p className="font-medium text-gray-900 dark:text-white mb-2">
                              <span className="text-pink-600 dark:text-pink-400 mr-2">{originalIndex + 1}.</span>
                              {pair.front}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              ↔ {pair.back}
                            </p>
                          </div>
                          <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleOpenEditPairModal(originalIndex)}
                              className="p-2 text-gray-400 dark:text-gray-500 hover:text-pink-600 dark:hover:text-pink-400 rounded-lg hover:bg-pink-50 dark:hover:bg-pink-900/30 transition-all"
                              title="Editar par"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeletePair(originalIndex)}
                              className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-all"
                              title="Deletar par"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Statistics Modal */}
      {showStatisticsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-6 border border-gray-200 dark:border-gray-700 max-w-md shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-xl bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Estatísticas do Match
              </h3>
              <button
                onClick={() => setShowStatisticsModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Attempts */}
              <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200/50 dark:border-blue-700/50">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-500/20 dark:bg-blue-500/30 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Tentativas</span>
                </div>
                <span className="text-2xl font-bold text-blue-900 dark:text-blue-100">{match.attemptsCount}</span>
              </div>
              
              {/* Best Time */}
              <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200/50 dark:border-green-700/50">
                <div className="flex items-center">
                  <div className="p-2 bg-green-500/20 dark:bg-green-500/30 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">Melhor Tempo</span>
                </div>
                <span className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {match.bestTime > 0 ? formatTime(match.bestTime) : '-'}
                </span>
              </div>
              
              {/* Average Time */}
              <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200/50 dark:border-purple-700/50">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-500/20 dark:bg-purple-500/30 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Tempo Médio</span>
                </div>
                <span className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  {match.averageTime > 0 ? formatTime(match.averageTime) : '-'}
                </span>
              </div>
              
              {/* Pairs Count */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200/50 dark:border-gray-600/50">
                <div className="flex items-center">
                  <div className="p-2 bg-gray-500/20 dark:bg-gray-500/30 rounded-lg mr-3">
                    <PuzzlePieceIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total de Pares</span>
                </div>
                <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{match.pairs.length}</span>
              </div>
              
              {/* Next Review */}
              <div className={`flex items-center justify-between p-4 rounded-xl border ${
                new Date(match.nextReview) <= new Date()
                  ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200/50 dark:border-orange-700/50'
                  : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200/50 dark:border-gray-600/50'
              }`}>
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg mr-3 ${
                    new Date(match.nextReview) <= new Date()
                      ? 'bg-orange-500/20 dark:bg-orange-500/30'
                      : 'bg-gray-500/20 dark:bg-gray-500/30'
                  }`}>
                    <svg className={`w-5 h-5 ${
                      new Date(match.nextReview) <= new Date()
                        ? 'text-orange-600 dark:text-orange-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className={`text-sm font-medium ${
                    new Date(match.nextReview) <= new Date()
                      ? 'text-orange-700 dark:text-orange-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {new Date(match.nextReview) <= new Date() ? 'Pronto para Revisar!' : 'Próxima Revisão'}
                  </span>
                </div>
                <span className={`text-lg font-bold ${
                  new Date(match.nextReview) <= new Date()
                    ? 'text-orange-900 dark:text-orange-100'
                    : 'text-gray-900 dark:text-gray-100'
                }`}>
                  {new Date(match.nextReview) <= new Date() 
                    ? 'Agora' 
                    : new Date(match.nextReview).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                  }
                </span>
              </div>
            </div>
            
            <div className="mt-6">
              <button
                onClick={() => setShowStatisticsModal(false)}
                className="w-full px-4 py-3 bg-gradient-to-r from-pink-500 via-purple-500 to-pink-600 hover:from-pink-600 hover:via-purple-600 hover:to-pink-700 text-white font-medium rounded-xl shadow-lg transition-all duration-200 hover:scale-105"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-96 shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4">
                Deletar Match?
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tem certeza que deseja deletar este match? Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteMatch}
                  className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                >
                  Deletar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Pair Modal */}
      {showCreatePairModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-6 border border-gray-200 dark:border-gray-700 max-w-2xl shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-xl bg-white dark:bg-gray-800 my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {editingPairIndex !== null ? 'Editar Par' : 'Novo Par'}
              </h3>
              <button
                onClick={handleClosePairModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Front (Term) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Termo *
                </label>
                <input
                  type="text"
                  value={newFront}
                  onChange={(e) => setNewFront(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400 focus:border-transparent transition-all"
                  placeholder="Ex: JavaScript"
                />
              </div>

              {/* Back (Definition) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Definição *
                </label>
                <textarea
                  value={newBack}
                  onChange={(e) => setNewBack(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-400 focus:border-transparent transition-all resize-none"
                  placeholder="Ex: Linguagem de programação para web"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleClosePairModal}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={savingPair}
              >
                Cancelar
              </button>
              <button
                onClick={handleSavePair}
                className="px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={savingPair || !newFront.trim() || !newBack.trim()}
              >
                {savingPair ? 'Salvando...' : (editingPairIndex !== null ? 'Salvar Alterações' : 'Criar Par')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchDetail;
